import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { PageMeta } from "@/components/PageMeta";
import { Trophy, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

type FieldErrors = { password?: string; confirmPassword?: string };

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("type=recovery")) {
      setRecoveryMode(true);
    }
  }, []);

  const mapSupabaseError = (message: string): FieldErrors => {
    const m = message.toLowerCase();
    if (m.includes("weak") || m.includes("pwned") || m.includes("at least") || m.includes("characters")) {
      return { password: t("auth.passwordRequirements") };
    }
    if (m.includes("match")) {
      return { confirmPassword: t("auth.passwordsDontMatch") };
    }
    return { password: message };
  };

  const handleSubmit = async () => {
    const errors: FieldErrors = {};
    if (password.length < 6) {
      errors.password = t("auth.passwordMinLength");
    } else if (!/\d/.test(password) && !/[^a-zA-Z0-9]/.test(password)) {
      errors.password = t("auth.passwordWeak");
    }
    if (password !== confirmPassword) {
      errors.confirmPassword = t("auth.passwordsDontMatch");
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (error) {
      setFieldErrors(mapSupabaseError(error.message));
    } else {
      setDone(true);
      toast.success(t("auth.passwordUpdated"));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero text-primary-foreground">
      <PageMeta
        title="Nové heslo — Tipomatic"
        description="Nastav si nové heslo k účtu Tipomatic a pokračuj v tipování zápasů."
        path="/reset-password"
      />
      <div className="absolute top-3 right-3"><LanguageSwitcher /></div>
      <div className="container max-w-md px-4 py-10">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2 text-2xl font-bold">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-accent text-accent-foreground shadow-gold">
            <Trophy className="h-5 w-5" />
          </div>
          {t("auth.title")}
        </Link>
        <p className="text-center mb-6 opacity-80">{t("auth.tagline")}</p>
        <Card className="text-foreground">
          <CardContent className="pt-6 space-y-4">
            {done ? (
              <div className="text-center space-y-3 py-4">
                <p className="text-lg font-medium">{t("auth.passwordUpdated")}</p>
                <p className="text-sm text-muted-foreground">{t("auth.signInWithNewPassword")}</p>
                <Button className="w-full h-11 bg-gradient-pitch" onClick={() => navigate("/auth")}>
                  {t("auth.signIn")}
                </Button>
              </div>
            ) : recoveryMode ? (
              <>
                <h1 className="text-lg font-semibold">{t("auth.setNewPassword")}</h1>
                <div className="space-y-2">
                  <Label>{t("auth.newPassword")}</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setFieldErrors((prev) => ({ ...prev, password: undefined }));
                    }}
                    minLength={6}
                    placeholder="••••••"
                    aria-invalid={!!fieldErrors.password}
                  />
                  {fieldErrors.password ? (
                    <p className="text-xs text-red-500">{fieldErrors.password}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">{t("auth.passwordRequirements")}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{t("auth.confirmNewPassword")}</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setFieldErrors((prev) => ({ ...prev, confirmPassword: undefined }));
                    }}
                    minLength={6}
                    placeholder="••••••"
                    aria-invalid={!!fieldErrors.confirmPassword}
                    onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                  />
                  {fieldErrors.confirmPassword && (
                    <p className="text-xs text-red-500">{fieldErrors.confirmPassword}</p>
                  )}
                </div>
                <Button
                  className="w-full h-11 bg-gradient-pitch"
                  disabled={busy || !password || !confirmPassword}
                  onClick={handleSubmit}
                >
                  {t("auth.updatePassword")}
                </Button>
              </>
            ) : (
              <div className="text-center space-y-3 py-4">
                <p className="text-sm text-muted-foreground">{t("auth.invalidOrExpiredLink")}</p>
                <Link
                  to="/auth"
                  className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  {t("auth.backToSignIn")}
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
